import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";
import { SITE } from "@/lib/i18n";
import { Calendar, MessageCircle, ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/f77d8b25-2e36-4798-bc80-de674de9479a/images/5a59a9aa275757a509d8030d9b613c989610242eb07f2ead649ccdc63902a4f3.png";

export const Hero = () => {
  const { t, lang } = useLang();
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  // Parallax: image moves down slower, content lifts, overlay darkens
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  const waText = encodeURIComponent(
    lang === "es" ? SITE.whatsapp_text_es : SITE.whatsapp_text_en
  );
  const waLink = `https://wa.me/${SITE.whatsapp_number}?text=${waText}`;

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0a0a0a] text-white"
    >
      {/* Parallaxed background image */}
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={{ y: imgY, scale: imgScale }}
      >
        <img
          src={HERO_IMG}
          alt="Sonrisa premium Multident"
          className="w-full h-full object-cover object-center"
        />
      </motion.div>

      {/* Overlay */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0) 100%), linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.78) 100%)",
          opacity: overlayOpacity,
        }}
      />

      {/* Grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Content */}
      <motion.div
        className="relative max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-24 min-h-[100svh] flex flex-col justify-end"
        style={{ y: contentY }}
      >
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[11px] md:text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
              {t.hero.overline}
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display font-extralight text-[52px] sm:text-7xl lg:text-[104px] xl:text-[128px] leading-[0.95] tracking-[-0.03em]"
          >
            <MaskedLines
              lines={[
                t.hero.title_a,
                <>
                  <span className="italic font-thin opacity-90">
                    {t.hero.title_b}
                  </span>
                </>,
                <span className="text-[#D4AF37]">{t.hero.title_c}</span>,
              ]}
              delay={0.25}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-sub"
            className="mt-10 text-base md:text-lg text-white/75 max-w-xl leading-relaxed font-body"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex flex-wrap gap-3"
          >
            <a
              data-testid="hero-cta-book"
              href="#contact"
              className="btn-premium inline-flex items-center gap-2 bg-white text-[#1A1A1A] px-7 py-4 rounded-full text-sm font-medium"
            >
              <Calendar size={16} />
              {t.hero.cta_book}
            </a>
            <a
              data-testid="hero-cta-whatsapp"
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="btn-premium inline-flex items-center gap-2 bg-transparent text-white border border-white/40 px-7 py-4 rounded-full text-sm font-medium hover:border-white"
            >
              <MessageCircle size={16} />
              {t.hero.cta_wa}
            </a>
            <a
              data-testid="hero-cta-cases"
              href="#cases"
              className="inline-flex items-center gap-2 text-white/80 hover:text-[#D4AF37] px-2 py-4 text-sm transition-colors"
            >
              {t.hero.cta_cases}
              <ArrowRight size={16} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1.2 }}
            className="mt-20 grid grid-cols-2 gap-4 max-w-xl border-t border-white/15 pt-6"
          >
            {[t.hero.stat_a, t.hero.stat_b].map((s, i) => (
              <p
                key={i}
                data-testid={`hero-stat-${i}`}
                className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-white/70 leading-snug"
              >
                {s}
              </p>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-white/60"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>

      {/* Side label */}
      <div className="hidden md:flex absolute right-6 bottom-10 items-center gap-3 -rotate-90 origin-bottom-right">
        <span className="h-px w-10 bg-[#D4AF37]" />
        <span className="text-xs tracking-[0.3em] text-white/70 uppercase">
          Multident · Veracruz · est. 2009
        </span>
      </div>
    </section>
  );
};
